import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Checkbox } from '@/components/ui/checkbox';
import { trpc } from '@/lib/trpc';
import { showSuccessToast, showErrorToast } from '@/hooks/use-mutation-with-toast';
import { Loader2 } from 'lucide-react';

const formSchema = z.object({
  name: z.string().min(1, 'Name is required'),
  description: z.string().optional(),
  code: z.string().optional(),
  imageUrl: z.string().optional(),
  badgeText: z.string().optional(),
  sortOrder: z.number(),
  isDefault: z.boolean(),
  isCalculated: z.boolean(),
  priceAdjustment: z.number().optional(),
});

type FormData = z.infer<typeof formSchema>;

interface VariantModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  packageId: string;
  variant?: {
    id: string;
    name: string;
    description: string | null;
    code: string | null;
    imageUrl: string | null;
    badgeText: string | null;
    sortOrder: number;
    isDefault: boolean;
    isCalculated: boolean;
    priceAdjustment: number | null;
    options: Array<{
      id: string;
      name: string;
      groupName: string;
      price: number;
      pricingUnit: string;
    }>;
  };
  onSuccess?: () => void;
}

export function VariantModal({
  open,
  onOpenChange,
  packageId,
  variant,
  onSuccess,
}: VariantModalProps) {
  const { t } = useTranslation('packages');
  const isEditing = !!variant;
  const [selectedOptionIds, setSelectedOptionIds] = useState<string[]>([]);

  const form = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: '',
      description: '',
      code: '',
      imageUrl: '',
      badgeText: '',
      sortOrder: 0,
      isDefault: false,
      isCalculated: true,
      priceAdjustment: undefined,
    },
  });

  // Fetch option groups to show available options
  const { data: groups } = trpc.packageOption.listGroups.useQuery(packageId);

  useEffect(() => {
    if (variant) {
      form.reset({
        name: variant.name,
        description: variant.description || '',
        code: variant.code || '',
        imageUrl: variant.imageUrl || '',
        badgeText: variant.badgeText || '',
        sortOrder: variant.sortOrder,
        isDefault: variant.isDefault,
        isCalculated: variant.isCalculated,
        priceAdjustment: variant.priceAdjustment ?? undefined,
      });
      setSelectedOptionIds(variant.options.map((o) => o.id));
    } else {
      form.reset({
        name: '',
        description: '',
        code: '',
        imageUrl: '',
        badgeText: '',
        sortOrder: 0,
        isDefault: false,
        isCalculated: true,
        priceAdjustment: undefined,
      });
      setSelectedOptionIds([]);
    }
  }, [variant, form]);

  const createMutation = trpc.packageOption.createVariant.useMutation({
    onSuccess: () => {
      showSuccessToast(t('variantModal.variantCreated'));
      onOpenChange(false);
      onSuccess?.();
    },
    onError: (error) => {
      showErrorToast(error.message || t('variantModal.createFailed'));
    },
  });

  const updateMutation = trpc.packageOption.updateVariant.useMutation({
    onSuccess: () => {
      showSuccessToast(t('variantModal.variantUpdated'));
      onOpenChange(false);
      onSuccess?.();
    },
    onError: (error) => {
      showErrorToast(error.message || t('variantModal.updateFailed'));
    },
  });

  const onSubmit = (data: FormData) => {
    const payload = {
      ...data,
      optionIds: selectedOptionIds,
      code: data.code || undefined,
      description: data.description || undefined,
      imageUrl: data.imageUrl || undefined,
      badgeText: data.badgeText || undefined,
      priceAdjustment: data.isCalculated ? undefined : data.priceAdjustment,
    };

    if (isEditing && variant) {
      updateMutation.mutate({
        id: variant.id,
        ...payload,
      });
    } else {
      createMutation.mutate({
        packageId,
        ...payload,
      });
    }
  };

  const toggleOption = (optionId: string) => {
    setSelectedOptionIds((prev) =>
      prev.includes(optionId)
        ? prev.filter((id) => id !== optionId)
        : [...prev, optionId]
    );
  };

  const isPending = createMutation.isPending || updateMutation.isPending;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {isEditing ? t('variantModal.editVariant') : t('variantModal.addVariant')}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="name">{t('variantModal.name')} *</Label>
              <Input
                id="name"
                placeholder={t('variantModal.namePlaceholder')}
                {...form.register('name')}
              />
              {form.formState.errors.name && (
                <p className="text-sm text-red-500">
                  {form.formState.errors.name.message}
                </p>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="code">{t('variantModal.code')}</Label>
              <Input
                id="code"
                placeholder={t('variantModal.codePlaceholder')}
                {...form.register('code')}
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="description">{t('variantModal.description')}</Label>
            <Textarea
              id="description"
              placeholder={t('variantModal.descriptionPlaceholder')}
              rows={2}
              {...form.register('description')}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="badgeText">{t('variantModal.badgeText')}</Label>
              <Input
                id="badgeText"
                placeholder={t('variantModal.badgePlaceholder')}
                {...form.register('badgeText')}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="sortOrder">{t('variantModal.sortOrder')}</Label>
              <Input
                id="sortOrder"
                type="number"
                {...form.register('sortOrder', { valueAsNumber: true })}
              />
            </div>
          </div>

          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label htmlFor="isDefault">{t('variantModal.defaultVariant')}</Label>
              <p className="text-sm text-muted-foreground">
                {t('variantModal.defaultVariantHint')}
              </p>
            </div>
            <Switch
              id="isDefault"
              checked={form.watch('isDefault')}
              onCheckedChange={(checked) => form.setValue('isDefault', checked)}
            />
          </div>

          <div className="border-t pt-4">
            <div className="flex items-center justify-between mb-4">
              <div className="space-y-0.5">
                <Label htmlFor="isCalculated">{t('variantModal.calculatePrice')}</Label>
                <p className="text-sm text-muted-foreground">
                  {t('variantModal.calculatePriceHint')}
                </p>
              </div>
              <Switch
                id="isCalculated"
                checked={form.watch('isCalculated')}
                onCheckedChange={(checked) => form.setValue('isCalculated', checked)}
              />
            </div>

            {!form.watch('isCalculated') && (
              <div className="space-y-2">
                <Label htmlFor="priceAdjustment">{t('variantModal.fixedPriceAdjustment')}</Label>
                <Input
                  id="priceAdjustment"
                  type="number"
                  step="0.01"
                  placeholder={t('variantModal.fixedPricePlaceholder')}
                  {...form.register('priceAdjustment', { valueAsNumber: true })}
                />
              </div>
            )}
          </div>

          <div className="border-t pt-4">
            <h4 className="font-medium mb-3">{t('variantModal.includedOptions')}</h4>
            {groups && groups.length > 0 ? (
              <div className="space-y-4">
                {groups.map((group) => (
                  <div key={group.id}>
                    <p className="text-sm font-medium text-muted-foreground mb-2">
                      {group.name}
                    </p>
                    <div className="space-y-2 pl-2">
                      {group.options.map((option) => (
                        <div
                          key={option.id}
                          className="flex items-center gap-3"
                        >
                          <Checkbox
                            id={option.id}
                            checked={selectedOptionIds.includes(option.id)}
                            onCheckedChange={() => toggleOption(option.id)}
                          />
                          <label
                            htmlFor={option.id}
                            className="flex-1 text-sm cursor-pointer"
                          >
                            {option.name}
                            <span className="text-muted-foreground ml-2">
                              ({new Intl.NumberFormat('en-US', {
                                style: 'currency',
                                currency: option.currency,
                              }).format(option.price)}
                              {option.pricingUnit === 'PER_PERSON'
                                ? '/person'
                                : option.pricingUnit === 'PERCENTAGE'
                                  ? '%'
                                  : ''})
                            </span>
                          </label>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">
                {t('variantModal.noOptionGroups')}
              </p>
            )}
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isPending}
            >
              {t('variantModal.cancel')}
            </Button>
            <Button type="submit" disabled={isPending}>
              {isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {isEditing ? t('variantModal.update') : t('variantModal.create')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
